import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Card, { CardBody, CardHeader, CardFooter } from '../components/Card';
import Button from '../components/Button';
import { ArrowLeftIcon, ClockIcon, CreditCardIcon, FileTextIcon, CheckCircleIcon, InfoIcon, UploadIcon, AlertCircleIcon, ChevronRightIcon, FileIcon, BuildingIcon, CheckSquareIcon, DollarSignIcon, SendIcon, RefreshCwIcon } from 'lucide-react';
import { services } from '../data/services';
import { mdas } from '../data/mdas';
// Define application steps
const APPLICATION_STEPS = [{
  id: 'details',
  name: 'Service Details',
  description: 'Review service information'
}, {
  id: 'requirements',
  name: 'Requirements',
  description: 'Check required documents'
}, {
  id: 'application',
  name: 'Application Form',
  description: 'Fill in your details'
}, {
  id: 'payment',
  name: 'Payment',
  description: 'Complete payment'
}, {
  id: 'confirmation',
  name: 'Confirmation',
  description: 'Review and submit'
}];
const ServiceDetailsPage: React.FC = () => {
  const {
    serviceId
  } = useParams<{
    serviceId: string;
  }>();
  const navigate = useNavigate();
  const [service, setService] = useState(services.find(s => s.id === serviceId));
  const [mda, setMda] = useState(service ? mdas.find(m => m.id === service.mdaId) : null);
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [formFiles, setFormFiles] = useState<Record<string, File | null>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isFormValid, setIsFormValid] = useState(false);
  const [acceptedDeclaration, setAcceptedDeclaration] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [applicationReference, setApplicationReference] = useState('');
  useEffect(() => {
    const currentService = services.find(s => s.id === serviceId);
    setService(currentService);
    if (currentService) {
      const currentMda = mdas.find(m => m.id === currentService.mdaId);
      setMda(currentMda || null);
      // Initialize form data
      const initialData: Record<string, string> = {};
      currentService.formFields.forEach(field => {
        if (field.type !== 'file') {
          initialData[field.id] = '';
        }
      });
      setFormData(initialData);
      // Initialize form files
      const initialFiles: Record<string, File | null> = {};
      currentService.formFields.forEach(field => {
        if (field.type === 'file') {
          initialFiles[field.id] = null;
        }
      });
      setFormFiles(initialFiles);
    }
  }, [serviceId]);
  // Update current step when active tab changes
  useEffect(() => {
    const stepIndex = APPLICATION_STEPS.findIndex(step => step.id === activeTab);
    if (stepIndex !== -1) {
      setCurrentStep(stepIndex);
    }
  }, [activeTab]);
  // Validate form when form data changes
  useEffect(() => {
    if (activeTab === 'application' && service) {
      const requiredFields = service.formFields.filter(field => field.required && field.type !== 'file');
      const requiredFiles = service.formFields.filter(field => field.required && field.type === 'file');
      const allRequiredFieldsFilled = requiredFields.every(field => formData[field.id] && formData[field.id].trim() !== '');
      const allRequiredFilesUploaded = requiredFiles.every(field => formFiles[field.id] !== null);
      setIsFormValid(allRequiredFieldsFilled && allRequiredFilesUploaded);
    }
  }, [formData, formFiles, activeTab, service]);
  // Generate a random application reference number
  useEffect(() => {
    if (!applicationReference && service) {
      const prefix = service.name.substring(0, 3).toUpperCase();
      const randomNum = Math.floor(10000 + Math.random() * 90000);
      const year = new Date().getFullYear();
      setApplicationReference(`${prefix}-${year}-${randomNum}`);
    }
  }, [service, applicationReference]);
  const handleInputChange = (fieldId: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };
  const handleFileChange = (fieldId: string, file: File | null) => {
    setFormFiles(prev => ({
      ...prev,
      [fieldId]: file
    }));
  };
  const handleDeclarationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAcceptedDeclaration(e.target.checked);
  };
  const moveToNextStep = () => {
    const nextStepIndex = currentStep + 1;
    if (nextStepIndex < APPLICATION_STEPS.length) {
      setActiveTab(APPLICATION_STEPS[nextStepIndex].id);
    }
  };
  const moveToPreviousStep = () => {
    const prevStepIndex = currentStep - 1;
    if (prevStepIndex >= 0) {
      setActiveTab(APPLICATION_STEPS[prevStepIndex].id);
    }
  };
  const handlePayment = () => {
    setIsProcessingPayment(true);
    // Simulate payment processing
    setTimeout(() => {
      setIsProcessingPayment(false);
      setPaymentComplete(true);
      moveToNextStep();
    }, 2000);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (activeTab === 'application') {
      if (isFormValid && acceptedDeclaration) {
        if (service && service.fee > 0) {
          moveToNextStep(); // Move to payment step
        } else {
          moveToNextStep(); // Skip payment, go to confirmation
          setPaymentComplete(true);
        }
      }
    } else if (activeTab === 'payment') {
      handlePayment();
    } else if (activeTab === 'confirmation') {
      submitApplication();
    }
  };
  const submitApplication = () => {
    setIsSubmitting(true);
    // Simulate application submission
    setTimeout(() => {
      setIsSubmitting(false);
      navigate(`/confirmation/${applicationReference}`, {
        state: {
          serviceId: service?.id,
          serviceName: service?.name,
          amount: service?.fee,
          reference: applicationReference,
          date: new Date().toLocaleDateString(),
          mda: mda?.name
        }
      });
    }, 1500);
  };
  if (!service || !mda) {
    return <div className="min-h-screen flex flex-col bg-gray-50" data-id="element-6103">
        <Header data-id="element-6104" />
        <main className="flex-grow py-8 px-4 sm:px-6 lg:px-8" data-id="element-6105">
          <div className="max-w-7xl mx-auto text-center py-12" data-id="element-6106">
            <h1 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-6107">
              Service Not Found
            </h1>
            <p className="text-gray-600 mb-6" data-id="element-6108">
              The service you're looking for doesn't exist.
            </p>
            <Link to="/mdas" data-id="element-6109">
              <Button variant="primary" data-id="element-6110">Browse Services</Button>
            </Link>
          </div>
        </main>
        <Footer data-id="element-6111" />
      </div>;
  }
  return <div className="min-h-screen flex flex-col bg-gray-50" data-id="element-6112">
      <Header data-id="element-6113" />
      <main className="flex-grow py-8 px-4 sm:px-6 lg:px-8" data-id="element-6114">
        <div className="max-w-7xl mx-auto" data-id="element-6115">
          {/* Page Header */}
          <div className="mb-8" data-id="element-6116">
            <Link to={`/mdas/${mda.id}/services`} className="inline-flex items-center text-green-600 hover:text-green-700 mb-4" data-id="element-6117">
              <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-6118" />
              Back to {mda.name} Services
            </Link>
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-6119">{service.name}</h1>
            <p className="text-gray-600 mt-1" data-id="element-6120">{service.description}</p>
            <div className="mt-4 flex flex-wrap gap-4" data-id="element-6121">
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800" data-id="element-6122">
                <CreditCardIcon className="h-4 w-4 mr-1" data-id="element-6123" />
                Fee: ₦{service.fee.toLocaleString()}
              </div>
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800" data-id="element-6124">
                <ClockIcon className="h-4 w-4 mr-1" data-id="element-6125" />
                Processing Time: {service.processingTime}
              </div>
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800" data-id="element-6126">
                <BuildingIcon className="h-4 w-4 mr-1" data-id="element-6127" />
                {mda.name}
              </div>
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800" data-id="element-6128">
                <FileIcon className="h-4 w-4 mr-1" data-id="element-6129" />
                Service ID: {service.id}
              </div>
            </div>
          </div>
          {/* Application Progress */}
          <div className="mb-8" data-id="element-6130">
            <div className="bg-white rounded-lg shadow p-4" data-id="element-6131">
              <div className="flex flex-wrap justify-between" data-id="element-6132">
                {APPLICATION_STEPS.map((step, index) => <div key={step.id} className={`flex flex-col items-center mb-4 ${index <= currentStep ? 'text-green-600' : 'text-gray-400'}`} style={{
                width: '20%'
              }} data-id="element-6133">
                    <div className="relative" data-id="element-6134">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${index < currentStep ? 'bg-green-600 border-green-600 text-white' : index === currentStep ? 'border-green-600 text-green-600' : 'border-gray-300 text-gray-400'}`} data-id="element-6135">
                        {index < currentStep ? <CheckIcon className="h-4 w-4" data-id="element-6136" /> : <span className="text-xs" data-id="element-6137">{index + 1}</span>}
                      </div>
                      {index < APPLICATION_STEPS.length - 1 && <div className={`absolute top-4 w-full h-0.5 ${index < currentStep ? 'bg-green-600' : 'bg-gray-200'}`} style={{
                    left: '100%',
                    transform: 'translateY(-50%)',
                    width: 'calc(100% - 2rem)'
                  }} data-id="element-6138"></div>}
                    </div>
                    <div className="mt-2 text-center" data-id="element-6139">
                      <p className={`text-xs font-medium ${index <= currentStep ? 'text-green-600' : 'text-gray-500'}`} data-id="element-6140">
                        {step.name}
                      </p>
                      <p className="text-xs hidden sm:block text-gray-400" data-id="element-6141">
                        {step.description}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Tab Content */}
          {activeTab === 'details' && <div className="bg-white shadow overflow-hidden sm:rounded-lg animate-fade-in" data-id="element-6142">
              <div className="px-4 py-5 sm:px-6" data-id="element-6143">
                <h2 className="text-lg leading-6 font-medium text-gray-900" data-id="element-6144">
                  Service Information
                </h2>
                <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-6145">
                  Details about this service and how to apply.
                </p>
              </div>
              <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-6146">
                <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2" data-id="element-6147">
                  <div className="sm:col-span-1" data-id="element-6148">
                    <dt className="text-sm font-medium text-gray-500" data-id="element-6149">
                      Service Name
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900" data-id="element-6150">
                      {service.name}
                    </dd>
                  </div>
                  <div className="sm:col-span-1" data-id="element-6151">
                    <dt className="text-sm font-medium text-gray-500" data-id="element-6152">
                      Provided By
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900" data-id="element-6153">{mda.name}</dd>
                  </div>
                  <div className="sm:col-span-1" data-id="element-6154">
                    <dt className="text-sm font-medium text-gray-500" data-id="element-6155">Fee</dt>
                    <dd className="mt-1 text-sm text-gray-900" data-id="element-6156">
                      ₦{service.fee.toLocaleString()}
                    </dd>
                  </div>
                  <div className="sm:col-span-1" data-id="element-6157">
                    <dt className="text-sm font-medium text-gray-500" data-id="element-6158">
                      Processing Time
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900" data-id="element-6159">
                      {service.processingTime}
                    </dd>
                  </div>
                  <div className="sm:col-span-2" data-id="element-6160">
                    <dt className="text-sm font-medium text-gray-500" data-id="element-6161">
                      Description
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900" data-id="element-6162">
                      {service.description}
                    </dd>
                  </div>
                  <div className="sm:col-span-2" data-id="element-6163">
                    <dt className="text-sm font-medium text-gray-500" data-id="element-6164">
                      Documents Required
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900" data-id="element-6165">
                      <ul className="list-disc pl-5 space-y-1" data-id="element-6166">
                        {service.requirements.map((req, index) => <li key={index} data-id="element-6167">{req}</li>)}
                      </ul>
                    </dd>
                  </div>
                </dl>
              </div>
              <div className="px-4 py-5 sm:px-6 bg-gray-50 flex justify-end" data-id="element-6168">
                <Button variant="primary" onClick={moveToNextStep} data-id="element-6169">
                  Continue to Requirements
                  <ChevronRightIcon className="ml-2 h-4 w-4" data-id="element-6170" />
                </Button>
              </div>
            </div>}
          {activeTab === 'requirements' && <div className="bg-white shadow overflow-hidden sm:rounded-lg animate-fade-in" data-id="element-6171">
              <div className="px-4 py-5 sm:px-6" data-id="element-6172">
                <h2 className="text-lg leading-6 font-medium text-gray-900" data-id="element-6173">
                  Required Documents
                </h2>
                <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-6174">
                  Please prepare these documents before proceeding with your
                  application.
                </p>
              </div>
              <div className="border-t border-gray-200" data-id="element-6175">
                <ul className="divide-y divide-gray-200" data-id="element-6176">
                  {service.requirements.map((requirement, index) => <li key={index} className="px-4 py-4 sm:px-6" data-id="element-6177">
                      <div className="flex items-start" data-id="element-6178">
                        <div className="flex-shrink-0" data-id="element-6179">
                          <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-6180" />
                        </div>
                        <div className="ml-3" data-id="element-6181">
                          <p className="text-sm font-medium text-gray-900" data-id="element-6182">
                            {requirement}
                          </p>
                        </div>
                      </div>
                    </li>)}
                </ul>
              </div>
              <div className="px-4 py-5 sm:px-6 bg-gray-50" data-id="element-6183">
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4" data-id="element-6184">
                  <div className="flex" data-id="element-6185">
                    <div className="flex-shrink-0" data-id="element-6186">
                      <InfoIcon className="h-5 w-5 text-yellow-400" data-id="element-6187" />
                    </div>
                    <div className="ml-3" data-id="element-6188">
                      <p className="text-sm text-yellow-700" data-id="element-6189">
                        All documents must be clear, legible, and in PDF, JPG,
                        or PNG format. Maximum file size is 5MB per document.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex justify-between" data-id="element-6190">
                  <Button variant="outline" onClick={moveToPreviousStep} data-id="element-6191">
                    Back to Details
                  </Button>
                  <Button variant="primary" onClick={moveToNextStep} data-id="element-6192">
                    Continue to Application
                    <ChevronRightIcon className="ml-2 h-4 w-4" data-id="element-6193" />
                  </Button>
                </div>
              </div>
            </div>}
          {activeTab === 'application' && <Card className="animate-fade-in" data-id="element-6194">
              <CardHeader data-id="element-6195">
                <h2 className="text-lg font-medium text-gray-900" data-id="element-6196">
                  Application Form
                </h2>
                <p className="text-sm text-gray-500 mt-1" data-id="element-6197">
                  Fill out the form below to apply for {service.name}
                </p>
              </CardHeader>
              <CardBody data-id="element-6198">
                <form onSubmit={handleSubmit} data-id="element-6199">
                  <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-6200">
                    {service.formFields.map(field => <div key={field.id} className={field.type === 'textarea' ? 'sm:col-span-6' : 'sm:col-span-3'} data-id="element-6201">
                        <label htmlFor={field.id} className="block text-sm font-medium text-gray-700" data-id="element-6202">
                          {field.label}{' '}
                          {field.required && <span className="text-red-500" data-id="element-6203">*</span>}
                        </label>
                        {field.type === 'text' && <input type="text" id={field.id} name={field.id} required={field.required} value={formData[field.id] || ''} onChange={e => handleInputChange(field.id, e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm" placeholder={field.placeholder} data-id="element-6204" />}
                        {field.type === 'email' && <input type="email" id={field.id} name={field.id} required={field.required} value={formData[field.id] || ''} onChange={e => handleInputChange(field.id, e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm" placeholder={field.placeholder} data-id="element-6205" />}
                        {field.type === 'number' && <input type="number" id={field.id} name={field.id} required={field.required} value={formData[field.id] || ''} onChange={e => handleInputChange(field.id, e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm" placeholder={field.placeholder} data-id="element-6206" />}
                        {field.type === 'date' && <input type="date" id={field.id} name={field.id} required={field.required} value={formData[field.id] || ''} onChange={e => handleInputChange(field.id, e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm" data-id="element-6207" />}
                        {field.type === 'select' && <select id={field.id} name={field.id} required={field.required} value={formData[field.id] || ''} onChange={e => handleInputChange(field.id, e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm" data-id="element-6208">
                            <option value="" data-id="element-6209">Select an option</option>
                            {field.options?.map(option => <option key={option} value={option} data-id="element-6210">
                                {option}
                              </option>)}
                          </select>}
                        {field.type === 'textarea' && <textarea id={field.id} name={field.id} rows={3} required={field.required} value={formData[field.id] || ''} onChange={e => handleInputChange(field.id, e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm" placeholder={field.placeholder} data-id="element-6211" />}
                        {field.type === 'file' && <div className="mt-1" data-id="element-6212">
                            <div className="flex items-center justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md" data-id="element-6213">
                              <div className="space-y-1 text-center" data-id="element-6214">
                                <div className="flex text-sm text-gray-600" data-id="element-6215">
                                  <label htmlFor={field.id} className="relative cursor-pointer bg-white rounded-md font-medium text-green-600 hover:text-green-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-green-500" data-id="element-6216">
                                    <span data-id="element-6217">Upload a file</span>
                                    <input id={field.id} name={field.id} type="file" className="sr-only" required={field.required} onChange={e => {
                              const file = e.target.files?.[0] || null;
                              handleFileChange(field.id, file);
                            }} data-id="element-6218" />
                                  </label>
                                  <p className="pl-1" data-id="element-6219">or drag and drop</p>
                                </div>
                                <p className="text-xs text-gray-500" data-id="element-6220">
                                  PNG, JPG, PDF up to 5MB
                                </p>
                              </div>
                            </div>
                            {formFiles[field.id] && <div className="mt-2 flex items-center text-sm text-green-600" data-id="element-6221">
                                <CheckCircleIcon className="h-4 w-4 mr-1" data-id="element-6222" />
                                {formFiles[field.id]?.name}
                              </div>}
                          </div>}
                      </div>)}
                  </div>
                  <div className="mt-8 border-t border-gray-200 pt-8" data-id="element-6223">
                    <div data-id="element-6224">
                      <h3 className="text-lg font-medium leading-6 text-gray-900" data-id="element-6225">
                        Declaration
                      </h3>
                      <p className="mt-1 text-sm text-gray-500" data-id="element-6226">
                        Please read and confirm the following declaration.
                      </p>
                    </div>
                    <div className="mt-4" data-id="element-6227">
                      <div className="flex items-start" data-id="element-6228">
                        <div className="flex items-center h-5" data-id="element-6229">
                          <input id="declaration" name="declaration" type="checkbox" required checked={acceptedDeclaration} onChange={handleDeclarationChange} className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded" data-id="element-6230" />
                        </div>
                        <div className="ml-3 text-sm" data-id="element-6231">
                          <label htmlFor="declaration" className="text-gray-700" data-id="element-6232">
                            I declare that the information provided in this
                            application is true and correct to the best of my
                            knowledge. I understand that providing false
                            information may result in the rejection of my
                            application and possible legal consequences.
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8 border-t border-gray-200 pt-8" data-id="element-6233">
                    <div className="flex justify-between items-center" data-id="element-6234">
                      <div data-id="element-6235">
                        <h3 className="text-lg font-medium leading-6 text-gray-900" data-id="element-6236">
                          Application Fee
                        </h3>
                        <p className="mt-1 text-sm text-gray-500" data-id="element-6237">
                          {service.fee > 0 ? 'You will be redirected to the payment page after submitting this form.' : 'There is no fee for this service.'}
                        </p>
                      </div>
                      <div className="text-2xl font-bold text-gray-900" data-id="element-6238">
                        ₦{service.fee.toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="mt-8 flex justify-between" data-id="element-6239">
                    <Button type="button" variant="outline" onClick={moveToPreviousStep} data-id="element-6240">
                      Back
                    </Button>
                    <Button type="submit" variant="primary" disabled={!isFormValid || !acceptedDeclaration} data-id="element-6241">
                      {service.fee > 0 ? <>
                          Continue to Payment
                          <ChevronRightIcon className="ml-2 h-4 w-4" data-id="element-6242" />
                        </> : <>
                          Continue to Confirmation
                          <ChevronRightIcon className="ml-2 h-4 w-4" data-id="element-6243" />
                        </>}
                    </Button>
                  </div>
                </form>
              </CardBody>
            </Card>}
          {activeTab === 'payment' && <div className="bg-white shadow sm:rounded-lg overflow-hidden animate-fade-in" data-id="element-6244">
              <div className="px-4 py-5 sm:px-6" data-id="element-6245">
                <h2 className="text-lg leading-6 font-medium text-gray-900" data-id="element-6246">
                  Payment Details
                </h2>
                <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-6247">
                  Complete your payment to proceed with your application
                </p>
              </div>
              <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-6248">
                <div className="mb-6" data-id="element-6249">
                  <h3 className="text-base font-medium text-gray-900 mb-4" data-id="element-6250">
                    Payment Summary
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg" data-id="element-6251">
                    <div className="flex justify-between mb-2" data-id="element-6252">
                      <span className="text-sm text-gray-600" data-id="element-6253">Service:</span>
                      <span className="text-sm font-medium" data-id="element-6254">
                        {service.name}
                      </span>
                    </div>
                    <div className="flex justify-between mb-2" data-id="element-6255">
                      <span className="text-sm text-gray-600" data-id="element-6256">
                        Application ID:
                      </span>
                      <span className="text-sm font-medium" data-id="element-6257">
                        {applicationReference}
                      </span>
                    </div>
                    <div className="flex justify-between mb-2" data-id="element-6258">
                      <span className="text-sm text-gray-600" data-id="element-6259">Fee:</span>
                      <span className="text-sm font-medium" data-id="element-6260">
                        ₦{service.fee.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between mb-2" data-id="element-6261">
                      <span className="text-sm text-gray-600" data-id="element-6262">
                        Processing Fee:
                      </span>
                      <span className="text-sm font-medium" data-id="element-6263">₦0.00</span>
                    </div>
                    <div className="border-t border-gray-200 my-2 pt-2 flex justify-between" data-id="element-6264">
                      <span className="text-base font-medium text-gray-900" data-id="element-6265">
                        Total:
                      </span>
                      <span className="text-base font-bold text-gray-900" data-id="element-6266">
                        ₦{service.fee.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mb-6" data-id="element-6267">
                  <h3 className="text-base font-medium text-gray-900 mb-4" data-id="element-6268">
                    Payment Methods
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-6269">
                    <div className="border border-green-500 rounded-lg p-4 bg-green-50" data-id="element-6270">
                      <div className="flex items-center mb-4" data-id="element-6271">
                        <input id="payment-card" name="payment-method" type="radio" checked={true} className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300" data-id="element-6272" />
                        <label htmlFor="payment-card" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-6273">
                          Credit/Debit Card
                        </label>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-6274">
                        <div className="flex space-x-2" data-id="element-6275">
                          <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/2560px-Visa_Inc._logo.svg.png" alt="Visa" className="h-6" data-id="element-6276" />
                          <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/1280px-Mastercard-logo.svg.png" alt="Mastercard" className="h-6" data-id="element-6277" />
                          <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/Verve_logo.svg/1200px-Verve_logo.svg.png" alt="Verve" className="h-6" data-id="element-6278" />
                        </div>
                        <CheckCircleIcon className="h-5 w-5 text-green-600" data-id="element-6279" />
                      </div>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4" data-id="element-6280">
                      <div className="flex items-center mb-4" data-id="element-6281">
                        <input id="payment-bank" name="payment-method" type="radio" disabled className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300" data-id="element-6282" />
                        <label htmlFor="payment-bank" className="ml-3 block text-sm font-medium text-gray-500" data-id="element-6283">
                          Bank Transfer
                        </label>
                      </div>
                      <p className="text-xs text-gray-500" data-id="element-6284">Coming soon</p>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4" data-id="element-6285">
                      <div className="flex items-center mb-4" data-id="element-6286">
                        <input id="payment-ussd" name="payment-method" type="radio" disabled className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300" data-id="element-6287" />
                        <label htmlFor="payment-ussd" className="ml-3 block text-sm font-medium text-gray-500" data-id="element-6288">
                          USSD Payment
                        </label>
                      </div>
                      <p className="text-xs text-gray-500" data-id="element-6289">Coming soon</p>
                    </div>
                  </div>
                </div>
                <div className="border-t border-gray-200 pt-6" data-id="element-6290">
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6" data-id="element-6291">
                    <div className="flex" data-id="element-6292">
                      <div className="flex-shrink-0" data-id="element-6293">
                        <AlertCircleIcon className="h-5 w-5 text-yellow-400" data-id="element-6294" />
                      </div>
                      <div className="ml-3" data-id="element-6295">
                        <p className="text-sm text-yellow-700" data-id="element-6296">
                          You will be redirected to our secure payment gateway
                          to complete this transaction. Your payment information
                          is encrypted and secure.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-4 py-5 sm:px-6 bg-gray-50 flex justify-between" data-id="element-6297">
                <Button type="button" variant="outline" onClick={moveToPreviousStep} data-id="element-6298">
                  Back to Application
                </Button>
                <Button type="button" variant="primary" onClick={handlePayment} disabled={isProcessingPayment} data-id="element-6299">
                  {isProcessingPayment ? <>
                      <RefreshCwIcon className="animate-spin h-4 w-4 mr-2" data-id="element-6300" />
                      Processing Payment...
                    </> : <>
                      <DollarSignIcon className="h-4 w-4 mr-2" data-id="element-6301" />
                      Make Payment (₦{service.fee.toLocaleString()})
                    </>}
                </Button>
              </div>
            </div>}
          {activeTab === 'confirmation' && <div className="bg-white shadow sm:rounded-lg overflow-hidden animate-fade-in" data-id="element-6302">
              <div className="px-4 py-5 sm:px-6 bg-green-50" data-id="element-6303">
                <div className="flex items-center" data-id="element-6304">
                  <CheckCircleIcon className="h-6 w-6 text-green-600 mr-3" data-id="element-6305" />
                  <h2 className="text-lg leading-6 font-medium text-green-900" data-id="element-6306">
                    Application Ready for Submission
                  </h2>
                </div>
                <p className="mt-1 max-w-2xl text-sm text-green-700" data-id="element-6307">
                  Please review your application details before final submission
                </p>
              </div>
              <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-6308">
                <h3 className="text-base font-medium text-gray-900 mb-4" data-id="element-6309">
                  Application Summary
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-6" data-id="element-6310">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6311">
                    <div data-id="element-6312">
                      <p className="text-sm font-medium text-gray-500" data-id="element-6313">
                        Application Reference
                      </p>
                      <p className="text-sm font-bold" data-id="element-6314">
                        {applicationReference}
                      </p>
                    </div>
                    <div data-id="element-6315">
                      <p className="text-sm font-medium text-gray-500" data-id="element-6316">
                        Service
                      </p>
                      <p className="text-sm" data-id="element-6317">{service.name}</p>
                    </div>
                    <div data-id="element-6318">
                      <p className="text-sm font-medium text-gray-500" data-id="element-6319">
                        Ministry/Department/Agency
                      </p>
                      <p className="text-sm" data-id="element-6320">{mda.name}</p>
                    </div>
                    <div data-id="element-6321">
                      <p className="text-sm font-medium text-gray-500" data-id="element-6322">Fee</p>
                      <p className="text-sm" data-id="element-6323">₦{service.fee.toLocaleString()}</p>
                    </div>
                    <div data-id="element-6324">
                      <p className="text-sm font-medium text-gray-500" data-id="element-6325">
                        Payment Status
                      </p>
                      <p className="text-sm" data-id="element-6326">
                        {paymentComplete ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-6327">
                            <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-6328" />
                            Paid
                          </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-6329">
                            <ClockIcon className="h-3 w-3 mr-1" data-id="element-6330" />
                            Pending
                          </span>}
                      </p>
                    </div>
                    <div data-id="element-6331">
                      <p className="text-sm font-medium text-gray-500" data-id="element-6332">
                        Expected Processing Time
                      </p>
                      <p className="text-sm" data-id="element-6333">{service.processingTime}</p>
                    </div>
                  </div>
                </div>
                <div className="mb-6" data-id="element-6334">
                  <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-6335">
                    Documents Submitted
                  </h4>
                  <ul className="border border-gray-200 rounded-lg divide-y divide-gray-200" data-id="element-6336">
                    {service.formFields.filter(field => field.type === 'file' && formFiles[field.id]).map((field, index) => <li key={index} className="px-4 py-3 flex items-center" data-id="element-6337">
                          <FileIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-6338" />
                          <span className="text-sm" data-id="element-6339">{field.label}</span>
                          <CheckCircleIcon className="h-4 w-4 text-green-600 ml-auto" data-id="element-6340" />
                        </li>)}
                  </ul>
                </div>
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6" data-id="element-6341">
                  <div className="flex" data-id="element-6342">
                    <div className="flex-shrink-0" data-id="element-6343">
                      <InfoIcon className="h-5 w-5 text-blue-400" data-id="element-6344" />
                    </div>
                    <div className="ml-3" data-id="element-6345">
                      <p className="text-sm text-blue-700" data-id="element-6346">
                        After submission, you can track your application status
                        using the reference number
                        <span className="font-bold" data-id="element-6347">
                          {' '}
                          {applicationReference}
                        </span>
                        . You will also receive notifications about your
                        application status.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-4 py-5 sm:px-6 bg-gray-50 flex justify-between" data-id="element-6348">
                <Button type="button" variant="outline" onClick={moveToPreviousStep} data-id="element-6349">
                  Back
                </Button>
                <Button type="button" variant="primary" onClick={submitApplication} disabled={isSubmitting || !paymentComplete} data-id="element-6350">
                  {isSubmitting ? <>
                      <RefreshCwIcon className="animate-spin h-4 w-4 mr-2" data-id="element-6351" />
                      Submitting...
                    </> : <>
                      <SendIcon className="h-4 w-4 mr-2" data-id="element-6352" />
                      Submit Application
                    </>}
                </Button>
              </div>
            </div>}
        </div>
      </main>
      <Footer data-id="element-6353" />
    </div>;
};
// Helper component for the CheckIcon
const CheckIcon = ({
  className
}: {
  className?: string;
}) => {
  return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-6354">
      <polyline points="20 6 9 17 4 12" data-id="element-6355"></polyline>
    </svg>;
};
export default ServiceDetailsPage;